/**
 * Method signature parameter rows for the signature editor grid.
 * Client `_rowId` values stay on grid rows and are removed before save.
 */

import { isValidVariableName, variableNameError } from './globalVarValue.js';

/** Selectable parameter types. `void` is a return type only. `float` is omitted; stored `double` is the fractional number. */
export const PARAM_TYPE_VALUES = ['string', 'bool', 'int', 'double', 'json'];

/** Return types include Nothing. Parameters do not. */
export const RETURN_TYPE_VALUES = ['void', ...PARAM_TYPE_VALUES];

/** Fresh empty object. A new instance each time the expression runs. */
export const JSON_EMPTY_OBJECT = 'new { }';

/** Flowchart object defaults. The stored value is the expression copied onto a new icon. */
export const FLOW_JSON_DEFAULT_OPTIONS = [
    { value: JSON_EMPTY_OBJECT, label: 'Empty object' },
    { value: 'json.NewArray()', label: 'Empty list' },
    { value: 'null', label: 'Nothing' },
];

const TYPE_FRIENDLY_NAMES = {
    void: 'Nothing',
    string: 'Plain text',
    bool: 'True or False',
    int: 'Whole number',
    double: 'Fractional number',
    json: 'Object',
};

/**
 * Display label for a stored type.
 * Flowcharts use the friendly name. API methods append the stored type.
 * Unknown stored values, including legacy `float`, stay as the raw token.
 */
export function formatTypeLabel(value, showStoredType) {
    const raw = value == null ? '' : String(value);
    const friendly = TYPE_FRIENDLY_NAMES[raw];
    if (!friendly)
        return raw;
    if (showStoredType)
        return `${friendly} (${raw})`;
    return friendly;
}

/** Default type for a new parameter. */
export const DEFAULT_PARAM_TYPE = 'string';

let rowSeq = 0;

export function nextParameterRowId() {
    rowSeq += 1;
    return `param-${rowSeq}`;
}

export function createParameterRow(overrides = {}) {
    const row = {
        Name: '',
        Type: DEFAULT_PARAM_TYPE,
        Description: '',
        Out: false,
        DefaultValue: '',
        ...overrides,
    };
    if (!row.Type)
        row.Type = DEFAULT_PARAM_TYPE;
    if (typeof row.Out !== 'boolean')
        row.Out = !!row.Out;
    if (row.Name == null)
        row.Name = '';
    if (row.Description == null)
        row.Description = '';
    if (row.DefaultValue == null)
        row.DefaultValue = '';
    if (!row._rowId)
        row._rowId = nextParameterRowId();
    return row;
}

/** Copy stored parameters into grid rows. Server copies never keep a client id. */
export function hydrateParameterRows(parameters) {
    const list = Array.isArray(parameters) ? parameters : [];
    return list.map((item) => {
        const src = { ...(item || {}) };
        delete src._rowId;
        return createParameterRow(src);
    });
}

/** Payload copy. Drops `_rowId` and keeps every other stored key. */
export function stripParameterRows(rows) {
    return (rows || []).filter((row) => row).map((row) => {
        const copy = { ...row };
        delete copy._rowId;
        return copy;
    });
}

/**
 * Rebuild row order after a managed drag.
 * Ids the grid did not report stay at the end, in their previous order.
 */
export function applyDragOrder(rows, orderedIds) {
    const list = rows || [];
    const byId = new Map();
    for (const row of list) {
        if (row && row._rowId != null && !byId.has(row._rowId))
            byId.set(row._rowId, row);
    }
    const next = [];
    const seen = new Set();
    for (const id of orderedIds || []) {
        const row = byId.get(id);
        if (!row || seen.has(id))
            continue;
        seen.add(id);
        next.push(row);
    }
    for (const row of list) {
        if (!row || seen.has(row._rowId))
            continue;
        seen.add(row._rowId);
        next.push(row);
    }
    return next;
}

/** Short line under the name field when the name is a normal identifier. */
export const METHOD_NAME_HINT = 'Used by callers and the web URL.';

/** Short line under the name field when the name is not a code-API identifier. Does not block save. */
export const METHOD_NAME_API_NOTE = 'Not callable from a code API.';

/** Hover text for the name-field info icon. */
export const METHOD_NAME_API_DETAIL = 'A code API can call this method when the name starts with a letter or underscore, then uses only letters, digits, and underscores. Any other name still saves and works in the designer.';

/** Blank name only. Non-standard names are allowed. */
export function methodNameError(name) {
    const n = name == null ? '' : String(name).trim();
    if (!n)
        return 'The method needs a name.';
    return null;
}

/** Soft note when the name cannot be used from a code API. Blank names are handled separately. */
export function methodNameApiNote(name) {
    const n = name == null ? '' : String(name).trim();
    if (!n || isValidVariableName(n))
        return null;
    return METHOD_NAME_API_NOTE;
}

/**
 * The grid can hold a copy of the row object (or a Vue proxy of it).
 * Match on `_rowId` so that copy is not counted as a second parameter.
 */
function isSameParameterRow(row, currentRow) {
    if (row === currentRow)
        return true;
    const id = currentRow?._rowId;
    return id != null && row?._rowId === id;
}

/** Cell editor: blank, illegal, or duplicate parameter name. */
export function parameterNameError(name, rows, currentRow) {
    const next = name == null ? '' : String(name).trim();
    if (!next)
        return 'Every parameter needs a name.';
    const message = variableNameError(next);
    if (message)
        return message;
    const dup = (rows || []).some((row) => !isSameParameterRow(row, currentRow) && String(row?.Name ?? '').trim() === next);
    if (dup)
        return `Duplicate name “${next}”.`;
    return null;
}

function parameterLabel(row) {
    const name = String(row?.Name ?? '').trim();
    return name ? `“${name}”` : 'This parameter';
}

/** Whole number, or a fractional/exponent form. Bare suffixes such as `0f` are rejected. */
function isNumberDefault(value) {
    if (/^-?\d+$/.test(value))
        return true;
    return /^-?\d+(?:\.\d+(?:[eE][+-]?\d+)?|[eE][+-]?\d+)[fFdD]?$/.test(value);
}

function bracesBalance(value) {
    let depth = 0;
    let quote = '';
    for (let i = 0; i < value.length; i++) {
        const c = value[i];
        if (quote) {
            if (c === '\\') {
                i += 1;
                continue;
            }
            if (c === quote)
                quote = '';
            continue;
        }
        if (c === '"' || c === "'") {
            quote = c;
            continue;
        }
        if (c === '{')
            depth += 1;
        else if (c === '}') {
            depth -= 1;
            if (depth < 0)
                return false;
        }
    }
    return depth === 0 && !quote;
}

/** `new { }`, `new {}`, or `new json { ... }` with balanced braces. */
export function isNewObjectExpression(value) {
    const text = value == null ? '' : String(value).trim();
    if (!/^new\s*(json\s*)?\{/.test(text) || !text.endsWith('}'))
        return false;
    return bracesBalance(text);
}

function unescapeCSharpString(inner) {
    return inner.replace(/\\(["\\nrt])/g, (_, c) => {
        if (c === 'n')
            return '\n';
        if (c === 'r')
            return '\r';
        if (c === 't')
            return '\t';
        return c;
    });
}

/** `json.Parse("...")` whose text is JSON. */
export function isJsonParseExpression(value) {
    const text = value == null ? '' : String(value).trim();
    const match = text.match(/^json\.Parse\(\s*"((?:\\.|[^"\\])*)"\s*\)$/);
    if (!match)
        return false;
    try {
        JSON.parse(unescapeCSharpString(match[1]));
        return true;
    }
    catch {
        return false;
    }
}

/**
 * Expression that can be copied onto an icon and compile to json.
 * Raw JSON text is not an expression.
 */
export function isJsonExpression(value) {
    const text = value == null ? '' : String(value).trim();
    if (!text)
        return false;
    if (text === 'null')
        return true;
    if (text === 'true' || text === 'false')
        return false;
    if (isNewObjectExpression(text))
        return true;
    if (/^json\.NewArray\(\s*\)$/.test(text))
        return true;
    if (/^json\.(True|False|Zero|One|Empty)$/.test(text))
        return true;
    if (isJsonParseExpression(text))
        return true;
    return isValidVariableName(text);
}

/**
 * When the text is raw JSON, the expression an API author can choose to save.
 * Returns null when the text is already an expression or is not JSON.
 */
export function jsonParseRewrite(value) {
    const text = value == null ? '' : String(value).trim();
    if (!text || isJsonExpression(text))
        return null;
    try {
        JSON.parse(text);
    }
    catch {
        return null;
    }
    const escaped = text.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `json.Parse("${escaped}")`;
}

/** Keep a json default when the type changes. Flowcharts keep the three choices or an object expression already stored. */
export function keepJsonDefault(value, api) {
    const text = value == null ? '' : String(value).trim();
    if (api)
        return isJsonExpression(text);
    if (FLOW_JSON_DEFAULT_OPTIONS.some((item) => item.value === text))
        return true;
    return isNewObjectExpression(text) || isJsonParseExpression(text);
}

export function flowJsonDefaultLabel(value) {
    const text = value == null ? '' : String(value);
    const option = FLOW_JSON_DEFAULT_OPTIONS.find((item) => item.value === text);
    return option ? option.label : text;
}

/**
 * Display text for a parameter default.
 * Flowchart object choices use the friendly label, including in the picker
 * (that formatter runs without a row, so pass `flowJson` for it).
 * API methods keep the stored expression.
 */
export function formatParameterDefault(value, { api = false, flowJson = false } = {}) {
    const text = value == null ? '' : String(value);
    if (api || !flowJson)
        return text;
    return flowJsonDefaultLabel(text);
}

/**
 * Default value for one parameter row.
 * Out parameters must name a variable. Non-string inputs must be a value of that type.
 * Object defaults are expressions. `options.api` selects the API wording.
 * String inputs may be blank.
 */
export function parameterDefaultError(row, options = {}) {
    if (!row)
        return null;
    const label = parameterLabel(row);
    const value = row.DefaultValue == null ? '' : String(row.DefaultValue).trim();
    if (row.Out) {
        if (!value)
            return `${label} needs a variable name as its default.`;
        if (!isValidVariableName(value))
            return `${label} needs a variable name as its default. Start with a letter or underscore, then use only letters, digits, and underscores.`;
        return null;
    }
    const type = row.Type == null ? '' : String(row.Type).trim();
    if (!type || type === 'string')
        return null;
    if (type === 'bool') {
        if (value !== 'true' && value !== 'false')
            return `${label} needs a default of true or false.`;
        return null;
    }
    if (type === 'int') {
        if (!/^-?\d+$/.test(value))
            return `${label} needs a whole number as its default.`;
        return null;
    }
    if (type === 'double' || type === 'float') {
        if (!isNumberDefault(value))
            return `${label} needs a number as its default.`;
        return null;
    }
    if (type === 'json') {
        if (isJsonExpression(value))
            return null;
        if (options.api)
            return `${label} needs code that creates an object, such as new { }, json.NewArray(), or null.`;
        return `${label} needs an empty object, an empty list, or nothing.`;
    }
    if (type === 'void')
        return `${label} needs a type other than Nothing.`;
    return `${label} needs another type. “${type}” cannot have a default value.`;
}

/** Save: first blank, illegal, or duplicate parameter name, then the first bad default. */
export function parameterRowsError(rows, options) {
    const list = rows || [];
    for (const row of list) {
        const message = parameterNameError(row?.Name, list, row);
        if (message)
            return message;
    }
    for (const row of list) {
        const message = parameterDefaultError(row, options);
        if (message)
            return message;
    }
    return null;
}
